import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@/lib/api/types';
import type { Airport } from '@/types/airport';
import type { AirportCriteria } from './api';
import { fetchAirportPage } from './api';

export type AirportsErrorKey =
  | 'airports.errorForbidden'
  | 'airports.errorSort'
  | 'airports.errorLoad';

/** What one completed request produced, tagged with the request it answered. */
interface Outcome {
  key: string;
  rows: Airport[];
  currPage: number;
  total: number;
  isLastPage: boolean;
  errorKey: AirportsErrorKey | null;
}

interface AirportListState {
  rows: Airport[];
  /** The page the server ACTUALLY returned, which is not always the one asked for. */
  currPage: number;
  total: number;
  isLastPage: boolean;
  isLoading: boolean;
  errorKey: AirportsErrorKey | null;
  /** Re-reads the current page — what a write calls once the server has committed. */
  reload: () => void;
}

/**
 * Runs the dictionary's query.
 *
 * **Loading is derived, not stored.** The only state is the outcome of the last
 * completed request tagged with the criteria it answered; "loading" is that tag not
 * matching what is being asked for now. Setting a flag at the top of the effect sets
 * state during the commit and cascades an extra render.
 *
 * It also gives the table the behaviour it wants for free: the previous rows stay on
 * screen while the next page loads, so the reader compares what they had against what
 * they asked for instead of watching it blink empty.
 *
 * Three failures are told apart, because they send the reader to three different
 * places: a **403** means this role may not read the dictionary and retrying will not
 * help, a **400** means the sort field was rejected, and everything else is a
 * connection worth trying again.
 */
export const useAirportList = (criteria: AirportCriteria): AirportListState => {
  // Serialized so the effect keys off the FILTER, not off the object identity —
  // which is new on every render. The nonce sits outside that serialization so a
  // reload is a new key while the criteria stay exactly as they were.
  const [nonce, setNonce] = useState(0);
  const body = JSON.stringify(criteria);
  const key = `${nonce}:${body}`;

  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetchAirportPage(JSON.parse(body) as AirportCriteria, controller.signal)
      .then((response) => {
        setOutcome({
          key,
          rows: Array.isArray(response.rows) ? response.rows : [],
          currPage: response.currPage ?? 1,
          total: response.rsCount ?? 0,
          // The server's own answer to "is there a next page", rather than a page
          // count computed from the total.
          isLastPage: response.lastBlock !== false,
          errorKey: null,
        });
      })
      .catch((caught: unknown) => {
        // An abort is not an error: a newer request is already in flight, and its
        // outcome is the one that should win.
        if (controller.signal.aborted) return;

        const status = caught instanceof ApiError ? caught.status : 0;

        setOutcome({
          key,
          rows: [],
          currPage: 1,
          total: 0,
          isLastPage: true,
          errorKey:
            status === 403
              ? 'airports.errorForbidden'
              : status === 400
                ? 'airports.errorSort'
                : 'airports.errorLoad',
        });
      });

    return () => controller.abort();
    // `body` is a substring of `key`, so it never changes without it — both are
    // listed because the effect reads both.
  }, [key, body]);

  const isCurrent = outcome?.key === key;
  const reload = useCallback(() => setNonce((current) => current + 1), []);

  return {
    // The previous page's rows stay visible while the next one loads. On an error
    // they are cleared: showing the last good answer under an error message would be
    // worse than showing none.
    rows: outcome?.errorKey ? [] : (outcome?.rows ?? []),
    currPage: outcome?.currPage ?? 1,
    total: outcome?.total ?? 0,
    isLastPage: outcome?.isLastPage ?? true,
    isLoading: !isCurrent,
    errorKey: isCurrent ? outcome.errorKey : null,
    reload,
  };
};
